// WorkingAbroadPreparation.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Briefcase, Target, CheckCircle, AlertCircle, Globe, Users, FileText } from 'lucide-react';

const WorkingAbroadPreparation = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <section className="relative py-20 bg-gradient-to-br from-[#202356] to-[#2542D4]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: `url(${require('../../assets/blog-2.jpg')})`,
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 text-sm font-light mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>July 18, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>10 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Working Abroad: How to Prepare for International Job Opportunities
            </h1>
            
            <p className="text-xl font-light leading-relaxed text-white/90">
              Working abroad requires more than finding an open role. Learn how to prepare for overseas employment, understand employer expectations, get interview ready, and organise the documentation needed to pursue work opportunities abroad with confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-4">Understanding International Employment</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Working internationally is a significant career move that offers tremendous opportunities for professional growth, cultural exposure, and financial advancement. However, it requires careful preparation, understanding of global job markets, and compliance with international employment regulations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Unlike domestic job searches, international employment involves additional layers of complexity including visa requirements, cultural adaptation, and long-distance coordination. This comprehensive guide will help you navigate these challenges and prepare effectively for overseas job opportunities.
              </p>
            </div>

            {/* Phase 1: Self-Assessment and Market Research */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Globe className="text-[#202356]" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-[#202356]">Phase 1: Self-Assessment and Market Research</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Evaluating Your Readiness</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Before pursuing international opportunities, assess your readiness for overseas employment:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Professional Qualifications:</strong> Your skills, experience, and certifications in the global context
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Language Proficiency:</strong> Communication skills for your target countries
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Cultural Adaptability:</strong> Your ability to work in diverse environments
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Financial Preparation:</strong> Savings for relocation and initial expenses
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Global Market Research</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Research should focus on countries with strong demand for your expertise and favorable employment policies:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-[#202356] mb-4">Key Research Areas:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Job Market Analysis</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Demand for your skills and expertise</li>
                      <li>• Salary expectations and cost of living</li>
                      <li>• Industry growth and stability</li>
                      <li>• Competition and qualification requirements</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Regulatory Environment</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Work visa and permit requirements</li>
                      <li>• Professional licensing and recognition</li>
                      <li>• Employment rights and protections</li>
                      <li>• Tax implications and benefits</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 2: Professional Consultation */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Users className="text-[#202356]" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-[#202356]">Phase 2: Professional Consultation</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Why Professional Guidance Matters</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                International employment consultants provide crucial insights that can significantly impact your success:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Market Intelligence:</strong> Real-time information about job openings and employer requirements
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Network Access:</strong> Connections with verified international employers
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Process Navigation:</strong> Expert guidance through complex visa and application procedures
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Eligibility Assessment</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                A thorough eligibility evaluation helps focus your efforts on realistic opportunities:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">High Priority</h4>
                  <p className="text-sm text-gray-600">Countries where your profile strongly matches employer requirements and visa criteria</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">Medium Priority</h4>
                  <p className="text-sm text-gray-600">Markets with good potential but requiring additional preparation or qualifications</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">Long-term Goals</h4>
                  <p className="text-sm text-gray-600">Aspirational markets that may require significant preparation or timing</p>
                </div>
              </div>
            </div>

            {/* Phase 3: Documentation and Preparation */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Phase 3: Documentation and Preparation</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Essential Documentation</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Proper documentation is critical for international employment applications:
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Professional Portfolio</h4>
                  <p className="text-gray-600">Updated resume/CV tailored for international standards, including achievements and metrics.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Educational Credentials</h4>
                  <p className="text-gray-600">Certified copies of degrees, diplomas, and professional certifications with translations if required.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Work Experience Verification</h4>
                  <p className="text-gray-600">Employment verification letters, reference letters, and portfolio of relevant work samples.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Personal Identification</h4>
                  <p className="text-gray-600">Valid passport, birth certificate, and any required identity verification documents.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Pro Tip:</h4>
                    <p className="text-gray-600">Create digital and physical copies of all documents. Many countries require specific formatting or notarization, so research requirements early in the process.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 4: Interview Preparation */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Phase 4: Interview Preparation</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">International Interview Formats</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                International interviews often follow different formats and expectations:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="space-y-4">
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Video Interviews</span>
                    <p className="text-sm text-gray-600 mt-1">Most common for initial screening, requiring technical setup and timezone coordination</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Technical Assessments</span>
                    <p className="text-sm text-gray-600 mt-1">Skills testing and practical exercises relevant to your role</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Cultural Fit Interviews</span>
                    <p className="text-sm text-gray-600 mt-1">Assessing adaptability and cross-cultural communication skills</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">Multi-stage Process</span>
                    <p className="text-sm text-gray-600 mt-1">Multiple rounds with different stakeholders and decision-makers</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Preparation Strategies</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Success in international interviews requires comprehensive preparation:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Research the Company Culture</strong> and work environment expectations
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Practice Cross-cultural Communication</strong> and appropriate business etiquette
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Prepare for Time Zone Differences</strong> and technical requirements
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Develop Questions About Relocation</strong> and integration support
                  </div>
                </li>
              </ul>
            </div>

            {/* Phase 5: Visa and Legal Preparation */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Phase 5: Visa and Legal Preparation</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Understanding Work Visa Categories</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Different countries offer various visa types for international workers:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Skilled Worker Visas</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Points-based systems for qualified professionals</li>
                    <li>• Employer-sponsored work permits</li>
                    <li>• Priority for in-demand occupations</li>
                    <li>• Pathways to permanent residency</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Temporary Work Arrangements</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Short-term assignments and projects</li>
                    <li>• Seasonal or industry-specific work</li>
                    <li>• Internship and training programs</li>
                    <li>• Digital nomad and remote work visas</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Application Process</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Visa applications require careful attention to detail and timing:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Gather Required Documents</strong> including job offer and sponsorship details
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Complete Medical Examinations</strong> and health certifications
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Submit Applications Early</strong> allowing time for processing delays
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Prepare for Interviews</strong> at consulates or immigration offices
                  </div>
                </li>
              </ul>
            </div>

            {/* Post-Offer Phase */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Post-Offer: Next Steps</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Evaluating Job Offers</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                International job offers require careful evaluation beyond salary:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Professional Factors</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Career advancement opportunities</li>
                    <li>• Professional development support</li>
                    <li>• Team composition and leadership</li>
                    <li>• Company culture and values</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Relocation Support</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Housing assistance and allowances</li>
                    <li>• Travel and shipping costs</li>
                    <li>• Healthcare and insurance coverage</li>
                    <li>• Family support and education benefits</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Pre-Departure Preparation</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Once you accept an offer, focus on smooth transition planning:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Arrange Housing and Accommodation</strong> in your destination country
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Manage Financial Transitions</strong> including banking and tax planning
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Research Cultural Integration</strong> and local customs
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Plan Healthcare Coverage</strong> and emergency preparations
                  </div>
                </li>
              </ul>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Conclusion: Your Global Career Journey</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Working abroad offers incredible opportunities for professional growth and cultural enrichment. While the process requires careful planning and preparation, the rewards can be life-changing. Success comes from thorough research, professional guidance, and strategic preparation.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Remember that international employment is a competitive field, and professional guidance can make the difference between a frustrating experience and a successful career move. Consider working with experienced consultants who understand global job markets and can help you navigate the complexities of international employment.
              </p>
              
              <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] p-8 rounded-lg text-white">
                <h3 className="text-2xl font-semibold mb-4">Ready to Explore International Career Opportunities?</h3>
                <p className="mb-6 text-white/90">
                  Our team specializes in connecting qualified professionals with verified international employers and guiding you through every step of the overseas employment process.
                </p>
                <button className="bg-white text-[#202356] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Book an Eligibility Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkingAbroadPreparation;
